import { useEffect, useRef, useState, type ReactNode } from 'react';
import { useParams } from 'react-router-dom';
import { PopUpButton, Spinner } from '../../ui/controls';
import { Icon, ICON_SIZE } from '../chrome/icons';
import { tip } from '../../ui/tooltip';
import { catalogLayers, discardFromCatalog, keepInCatalog, listCatalog, type CatalogItem, type CatalogKind, type CatalogPlace, type SavePlace } from '../../services/catalog';
import { useWork } from '../work';
import { KitWall } from './brand';
import { tileRatio } from './columns';
import { aimed, Body, Heading, Quiet, Wall, type LibraryActions } from './kinds';
import { carry } from '../carry';
import { CatalogPreview } from './CatalogTilePreview';
import { LibraryPagination } from './parts';
import { LibraryFilters } from './LibraryFilters';
import { askPage, worthIt } from '../ai/pageSmarts';

/**
 * Templates and Graphics share catalogue search and filter controls. The file's project,
 * personal library, workspace and Flobi provide the results; Graphics also offers the
 * current brand's drawings. Picking a tile places its real SVG or saved layers.
 */

interface Shelf {
  readonly items: CatalogItem[];
  readonly status: 'loading' | 'ready' | 'failed';
  readonly problem?: string;
  readonly moreAspectMatches?: boolean;
}
const EMPTY: Shelf = { items: [], status: 'loading' };
type Cursors = Record<CatalogPlace, number | null>;
const NO_MORE: Cursors = { project: null, mine: null, workspace: null, flobi: null };

/** The chip that lists Flobi's newest first. Not a category's name: no category can start with an asterisk here. */
const RECENT = '*recent';
/** How many of the newest are "recently added". */
const RECENT_MOST = 24;

/** What a kind last showed for a project, kept for the visit: coming back to a panel is not an empty panel. */
const recent = new Map<string, { shelves: Record<CatalogPlace, Shelf>; next: Cursors; categories: readonly string[] }>();

/** A picture still being drawn is asked for again this often, this many times. */
const REDRAW_MS = 4_000;
const REDRAW_TRIES = 5;

/**
 * One kind's shelves for this file: its project's, mine, the workspace's and Flobi's —
 * read together when the panel opens, narrowed by the field, and each read on as the
 * list is scrolled.
 */
function useShelves(kind: CatalogKind, projectId: string | null, places: readonly CatalogPlace[], aspectRatio?: number) {
  const [source, setSource] = useState<CatalogPlace | 'all'>('all');
  const wanted = places.filter(place => source === 'all' || place === source).join(',');
  const key = `${kind}:${projectId ?? ''}:${wanted}:${aspectRatio ?? ''}`;
  const [query, setQuery] = useState('');
  /** Which chip is on: all of it (''), Flobi's newest (RECENT), or one of Flobi's categories by name. */
  const [filter, setFilter] = useState('');
  const [again, setAgain] = useState(0);
  const [state, setState] = useState(() => recent.get(key) ?? { shelves: { project: EMPTY, mine: EMPTY, workspace: EMPTY, flobi: EMPTY }, next: NO_MORE, categories: [] as readonly string[] });
  // What narrows Flobi's shelf, as the list is asked for it.
  const narrowed = filter === RECENT ? { recent: true, limit: RECENT_MOST } : filter ? { category: filter } : {};
  /** Which question the answer on screen belongs to: a slow answer to an old one is dropped. */
  const asked = useRef(0);
  const tries = useRef(0);
  const reading = useRef<number | null>(null);
  const initial = useRef(true);
  const [pagination, setPagination] = useState<{ loading: boolean; problem?: string }>({ loading: false });
  useEffect(() => {
    const refresh = () => { tries.current = 0; setAgain(n => n + 1); };
    window.addEventListener('artwork:catalog-changed', refresh);
    return () => window.removeEventListener('artwork:catalog-changed', refresh);
  }, []);
  useEffect(() => {
    const mine = ++asked.current;
    initial.current = true;
    reading.current = null;
    setPagination({ loading: false });
    // Source tabs show their own results while loading, never the previous source's tiles.
    setState(was => recent.get(key) && !query.trim() && !filter ? recent.get(key)! : {
      shelves: Object.fromEntries(['project', 'mine', 'workspace', 'flobi'].map(place => [place,
        wanted.split(',').includes(place) && (!filter || place === 'flobi') ? EMPTY : { items: [], status: 'ready' },
      ])) as Record<CatalogPlace, Shelf>,
      next: NO_MORE,
      categories: was.categories,
    });
    const search = query.trim();
    // Typing is not asking: the shelves are read once the hand stops.
    const wait = setTimeout(() => {
      void Promise.all(
        // A chip is about Flobi's: nothing a person saved is filed under a category.
        (wanted.split(',') as CatalogPlace[]).filter((place) => !filter || place === 'flobi').map(async (place) => {
          try {
            const page = await listCatalog(kind, { place, ...(projectId ? { projectId } : {}), search, aspectRatio, ...(place === 'flobi' ? narrowed : {}) });
            // The newest are a fixed few: there is no "more" of them.
            return { place, shelf: { items: page.items, status: 'ready', moreAspectMatches: page.moreAspectMatches } as Shelf, next: place === 'flobi' && filter === RECENT ? null : page.next, categories: page.categories };
          } catch (error) {
            return { place, shelf: { items: [], status: 'failed', problem: error instanceof Error ? error.message : 'The catalogue could not be read.' } as Shelf, next: null, categories: null };
          }
        }),
      ).then((answers) => {
        if (asked.current !== mine) return;
        initial.current = false;
        const shelves: Record<CatalogPlace, Shelf> = { project: { items: [], status: 'ready' }, mine: { items: [], status: 'ready' }, workspace: { items: [], status: 'ready' }, flobi: { items: [], status: 'ready' } };
        const cursors: Cursors = { ...NO_MORE };
        for (const answer of answers) { shelves[answer.place] = answer.shelf; cursors[answer.place] = answer.next; }
        const flobi = answers.find((answer) => answer.place === 'flobi');
        setState((was) => {
          // A read that failed says nothing about the categories: the chips stay as they were.
          const next = { shelves, next: cursors, categories: flobi?.categories ?? was.categories };
          if (!search && !filter) recent.set(key, next);
          return next;
        });
      });
    }, query ? 280 : 0);
    return () => { clearTimeout(wait); if (asked.current === mine) asked.current++; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- `narrowed` is `filter`, said another way.
  }, [kind, projectId, key, query, filter, again, wanted, aspectRatio]);

  // A category staff have emptied or renamed is no longer a chip: back to all of it.
  useEffect(() => {
    if (filter && filter !== RECENT && state.categories.length > 0 && !state.categories.includes(filter)) setFilter('');
  }, [filter, state.categories]);

  // A picture is drawn the first time anybody asks for it: asked again shortly, it is there.
  const waiting = Object.values(state.shelves).some((shelf) => shelf.items.some((one) => one.thumbnailPending));
  useEffect(() => {
    if (!waiting || tries.current >= REDRAW_TRIES) return;
    const timer = setTimeout(() => {
      tries.current += 1;
      setAgain((n) => n + 1);
    }, REDRAW_MS);
    return () => clearTimeout(timer);
  }, [waiting, again]);

  const more = (retry = false) => {
    const place = places.find(place => state.next[place] !== null && state.shelves[place].moreAspectMatches) ?? places.find(place => state.next[place] !== null);
    if (!place || initial.current || reading.current !== null || (pagination.problem && !retry)) return;
    const mine = asked.current;
    reading.current = mine;
    setPagination({ loading: true });
    listCatalog(kind, { place, ...(projectId ? { projectId } : {}), search: query.trim(), skip: state.next[place]!, aspectRatio, ...(place === 'flobi' ? narrowed : {}) })
      .then((page) => {
        if (asked.current === mine) setState((was) => {
          const next = { ...was, next: { ...was.next, [place]: page.next }, shelves: { ...was.shelves, [place]: { items: [...was.shelves[place].items, ...page.items], status: 'ready' as const, moreAspectMatches: page.moreAspectMatches } } };
          if (!query.trim() && !filter) recent.set(key, next);
          return next;
        });
      })
      .catch(() => {
        if (asked.current === mine) setPagination({ loading: false, problem: 'More items could not be loaded. Try again.' });
      })
      .finally(() => {
        if (reading.current === mine) reading.current = null;
        if (asked.current === mine) setPagination(was => ({ ...was, loading: false }));
      });
  };

  const reload = () => {
    tries.current = 0;
    setAgain((n) => n + 1);
  };
  /** Asked as often as it is asked and still not drawn: the tiles stop saying it is coming (review E9). */
  const gaveUp = waiting && tries.current >= REDRAW_TRIES;
  return { query, setQuery, filter, setFilter, source, setSource: (next: CatalogPlace | 'all') => { setFilter(''); setSource(next); }, categories: state.categories, shelves: state.shelves, more, reload, gaveUp,
    hasMore: Object.values(state.next).some(cursor => cursor !== null), pagination };
}

/** Search and source/category filters share one control layout across the catalogue. */
function CatalogFilters({ kind, query, onQuery, source, onSource, filter, onFilter, categories, project }: {
  kind: CatalogKind;
  query: string;
  onQuery: (query: string) => void;
  source: CatalogPlace | 'all';
  onSource: (source: CatalogPlace | 'all') => void;
  filter: string;
  onFilter: (filter: string) => void;
  categories: readonly string[];
  project: boolean;
}) {
  const noun = kind === 'template' ? 'Template' : 'Graphic';
  const plural = kind === 'template' ? 'templates' : 'graphics';
  const count = Number(source !== 'all') + Number(!!filter);
  return <LibraryFilters query={query} onQuery={onQuery} searchLabel={`Search ${plural}`} filterLabel={`Filter ${plural}`} dialogLabel={`${noun} filters`} count={count} onReset={() => onSource('all')}>
    <div className="catalog-filter-field"><span>{noun}s from</span><PopUpButton label={`${noun} source`} value={source} onChange={onSource} className="catalog-filter-picker" options={[
      ['all', `All ${plural}`], ['flobi', `Flobi ${plural}`], ['mine', `My ${plural}`], ['workspace', 'Workspace'], ...(project ? [['project', 'This project'] as const] : []),
    ]} /></div>
    {(source === 'all' || source === 'flobi') && categories.length > 0 && <div className="catalog-filter-field"><span>Category</span><PopUpButton label={`${noun} category`} value={filter} onChange={value => { if (value) onSource('flobi'); onFilter(value); }} className="catalog-filter-picker" options={[
      ['', 'All categories'], ...(kind === 'graphic' ? [[RECENT, 'Recently added'] as const] : []), ...categories.map(category => [category, category] as const),
    ]} /></div>}
  </LibraryFilters>;
}

// ── Saving ──────────────────────────────────────────────────────────────────

/**
 * Keeps what is chosen as a graphic — and says where it
 * lives. "Only Me" is the person's alone; the project's is for everybody on the project;
 * the workspace's for everybody in it. Those three and no more: nothing saved here goes
 * to every workspace. Flobi's own catalogue is filled from Flobi Admin, where staff add
 * what they saved as Only Me (Tarek, 2026-09-30: "why is this still an option?").
 */
function Keep({ actions, onSaved }: { actions: LibraryActions; onSaved: () => void }) {
  const { context } = useWork();
  const project = context?.project && context.canEdit ? context.project : null;
  const [open, setOpen] = useState(false);
  const [name, setName] = useState('');
  const [place, setPlace] = useState<SavePlace>('mine');
  const [saving, setSaving] = useState(false);
  const clip = () => actions.clipSelection();

  const begin = () => {
    const found = clip();
    if (!found) {
      actions.notice('Select the objects to save as a graphic first.');
      return;
    }
    setName(found.name);
    // A file in a project is working for the project: that is where its templates go, unless told otherwise.
    setPlace(project ? 'project' : 'mine');
    setOpen(true);
  };

  const places: [SavePlace, string][] = [['mine', 'Only Me'], ...(project ? [['project', project.name] as [SavePlace, string]] : []), ['workspace', 'Workspace']];

  const save = async () => {
    // Read again now: what is on the page when Save is pressed is what is kept.
    const found = clip();
    const called = name.trim();
    if (!found || !called || saving) return;
    setSaving(true);
    try {
      await keepInCatalog({ kind: 'graphic', name: called, place, ...(place === 'project' && project ? { projectId: project.id } : {}), layers: found.layers, width: found.width, height: found.height });
      setOpen(false);
      const where = places.find(([value]) => value === place)?.[1] ?? '';
      actions.notice(place === 'mine' ? `“${called}” is saved for you.` : `“${called}” is saved to ${where}.`);
      onSaved();
    } catch (error) {
      actions.notice(error instanceof Error ? error.message : 'That could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button type="button" onClick={begin} className="graphics-save-selection">
        <Icon name="plus" size={ICON_SIZE.small} />Save selection as graphic
      </button>
    );
  }
  return (
    <form
      className="graphics-save-form grid shrink-0 grid-cols-[minmax(0,1fr)] gap-[8px] rounded-[12px] bg-fill-1 p-[10px]"
      onSubmit={(event) => {
        event.preventDefault();
        void save();
      }}
    >
      <input autoFocus value={name} onChange={(event) => setName(event.target.value)} placeholder="Name" aria-label="Name" maxLength={120} className="field-focus h-[32px] w-full rounded-[9px] bg-fill-2 px-[10px] text-[13px] text-label outline-none placeholder:text-label-3" />
      <div className="grid grid-cols-[auto_minmax(0,1fr)] items-center gap-[10px] pl-[2px] text-[12.5px] text-label-2">
        <span>Save To</span>
        <PopUpButton label="Save To" value={place} options={places} onChange={setPlace} className="h-[30px]" />
      </div>
      <div className="flex gap-[8px]">
        <button type="button" onClick={() => setOpen(false)} className="h-[32px] flex-1 rounded-full bg-fill-2 text-[12.5px] font-medium text-label hover:bg-fill-selected">
          Cancel
        </button>
        <button type="submit" disabled={saving || !name.trim()} className="h-[32px] flex-1 rounded-full bg-accent text-[12.5px] font-semibold text-on-accent hover:bg-accent-hover disabled:opacity-50">
          <span className="inline-flex items-center gap-[6px]">
            Save
            {saving ? <Spinner size={11} /> : null}
          </span>
        </button>
      </div>
    </form>
  );
}

/**
 * What a tile wears when the person may take it out of its place: a quiet ×, and one
 * question before it goes — it is gone for everybody it was shared with.
 */
function Removable({ item, onRemoved, actions, children }: { item: CatalogItem; onRemoved: () => void; actions: LibraryActions; children: ReactNode }) {
  const [asking, setAsking] = useState(false);
  if (!item.canRemove) return <>{children}</>;
  const remove = async () => {
    try {
      await discardFromCatalog(item.id);
      onRemoved();
    } catch (error) {
      actions.notice(error instanceof Error ? error.message : 'That could not be removed.');
    } finally {
      setAsking(false);
    }
  };
  return (
    <div className="group/tile relative">
      {children}
      {asking ? (
        <div className="absolute inset-0 grid place-content-center gap-[6px] rounded-[9px] bg-[rgb(0_0_0/0.62)] p-[8px]">
          <button type="button" onClick={() => void remove()} className="h-[26px] rounded-full bg-white px-[12px] text-[12px] font-semibold text-black">
            Remove
          </button>
          <button type="button" onClick={() => setAsking(false)} className="h-[26px] rounded-full px-[12px] text-[12px] font-medium text-white">
            Keep
          </button>
        </div>
      ) : (
        <button
          type="button"
          aria-label={`Remove ${item.name}`}
          {...tip(item.place === 'mine' ? 'Remove' : 'Remove for Everyone')}
          onClick={() => setAsking(true)}
          className="touch-ring absolute right-[5px] top-[5px] grid size-[22px] place-items-center rounded-full bg-[rgb(0_0_0/0.55)] text-[14px] leading-none text-white opacity-0 transition-opacity focus-visible:opacity-100 group-hover/tile:opacity-100 pointer-coarse:opacity-100"
        >
          ×
        </button>
      )}
    </div>
  );
}

// ── Templates ───────────────────────────────────────────────────────────────

/** How long the panel waits on Flobi's suggestion before it shows the rest without one. */
const SUGGEST_PATIENCE_MS = 4_000;

/**
 * Whole pages to start from. Pressing one turns an empty page into it, or adds it as a
 * new page beside the others when the page has work on it.
 */
export function TemplatesPanel({ actions }: { actions: LibraryActions }) {
  const { context } = useWork();
  const project = context?.project ?? null;
  const board = actions.currentArtboard?.();
  const aspectRatio = board && board.width > 0 && board.height > 0 && Number.isFinite(board.width / board.height) ? board.width / board.height : undefined;
  const { query, setQuery, filter, setFilter, source, setSource, categories, shelves, more, reload, gaveUp, hasMore, pagination } = useShelves('template', project?.id ?? null, project ? ['project', 'mine', 'workspace', 'flobi'] : ['mine', 'workspace', 'flobi'], aspectRatio);
  const items = Object.values(shelves).flatMap(shelf => shelf.items);
  const fits = (item: CatalogItem) => aspectRatio !== undefined && (item.aspectMatch ?? Math.abs(item.width / item.height / aspectRatio - 1) <= 0.005);
  const matching = items.filter(fits);
  // Only the whole catalogue gets a "for you": a chosen place, chip or search is the person's own narrowing.
  const browsing = source === 'all' && !filter && !query.trim();
  // Flobi's category for this file (Jev, from what the file is called and its page size), read on its own
  // and shown above everything else, so the panel opens on what suits the page rather than on one narrow
  // chip (Tarek, 2026-11-02: "load the suggested templates first, then the rest, cause it feels empty").
  const [suggested, setSuggested] = useState<{ status: 'asking' } | { status: 'done'; items: CatalogItem[] }>({ status: 'asking' });
  const suggestedItems = suggested.status === 'done' ? [...suggested.items.filter(fits), ...suggested.items.filter(item => !fits(item))] : [];
  const forYou = browsing ? [...new Map([...suggestedItems, ...matching].map(item => [item.id, item])).values()] : matching;
  const forYouIds = new Set(forYou.map(item => item.id));
  const other = items.filter(item => !forYouIds.has(item.id));
  const asking = browsing && suggested.status === 'asking' && items.length > 0;
  // Every shelf finishes its matching pages before any other aspect ratios are shown.
  const waitingForMatches = Object.values(shelves).some(shelf => shelf.moreAspectMatches);
  const loading = Object.values(shelves).some(shelf => shelf.status === 'loading');
  const failed = Object.values(shelves).find(shelf => shelf.status === 'failed');
  const [opening, setOpening] = useState<string | null>(null);
  // Asked once the categories arrive; read again only when the artboard's shape changes.
  const { documentId } = useParams<{ documentId: string }>();
  const settled = Object.values(shelves).every(shelf => shelf.status !== 'loading');
  useEffect(() => {
    if (!documentId) { setSuggested({ status: 'done', items: [] }); return; }
    // No categories once the shelves are read: there is nothing to suggest from.
    if (!categories.length) { if (settled) setSuggested({ status: 'done', items: [] }); return; }
    let live = true;
    setSuggested({ status: 'asking' });
    // A slow answer does not hold the rest of the panel back for long.
    const patience = new Promise<null>(done => setTimeout(() => done(null), SUGGEST_PATIENCE_MS));
    void Promise.race([askPage('templates', documentId.toLowerCase(), actions.pageSummary?.(), { categories }), patience])
      .then(async (answer) => {
        if (!worthIt(answer) || !categories.includes(answer!.value)) return [];
        return (await listCatalog('template', { place: 'flobi', category: answer!.value, aspectRatio })).items;
      })
      .catch(() => [] as CatalogItem[])
      .then((found) => { if (live) setSuggested({ status: 'done', items: found }); });
    return () => { live = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- the categories are compared by what they say.
  }, [documentId, categories.join('\n'), aspectRatio, settled && !categories.length]);

  const pick = async (item: CatalogItem, acts = actions) => {
    const card = { name: item.name, width: item.width, height: item.height, background: item.background };
    if (item.format === 'svg') {
      acts.placeTemplate({ ...card, svg: item.svg });
      return;
    }
    if (opening) return;
    setOpening(item.id);
    try {
      const layers = await catalogLayers(item.id);
      if (layers) acts.placeTemplate({ ...card, layers });
      else acts.notice('That template could not be opened.');
    } catch (error) {
      acts.notice(error instanceof Error ? error.message : 'That template could not be opened.');
    } finally {
      setOpening(null);
    }
  };

  const wall = (cards: CatalogItem[]) => <Wall items={cards} ratio={item => item.width / item.height} tile={item => (
    <Removable key={item.id} item={item} actions={actions} onRemoved={reload}>
      <button type="button" aria-label={item.name} aria-busy={opening === item.id} {...tip(item.name)} onClick={() => void pick(item)} {...carry(at => void pick(item, aimed(actions, at)))} className="library-card catalog-card aria-busy:opacity-60">
        <span className="library-card-preview" style={{ aspectRatio: String(tileRatio(item.width / item.height)), background: item.background ?? '#ffffff' }}>
          {item.thumbnailUrl || item.svg || item.format === 'layers' ? <CatalogPreview item={item} fit="cover" /> : item.thumbnailPending && !gaveUp ? (
            <span role="status" aria-label="Drawing its picture" className="grid size-full place-items-center text-label-3"><Spinner size={16} /></span>
          ) : <span className="grid size-full place-items-center text-[#b8b8be]"><Icon name="artboard" size={18} /></span>}
        </span>
      </button>
    </Removable>
  )} />;

  return <>
    <CatalogFilters kind="template" query={query} onQuery={setQuery} source={source} onSource={setSource} filter={filter} onFilter={setFilter} categories={categories} project={!!project} />
    <Body onEnd={more}>
      {loading && !items.length ? <div role="status" aria-label="Loading templates"><div aria-hidden><Wall items={[1, 1, 0.8, 0.5625]} ratio={one => one} tile={one => <div key={one} className="rounded-[9px] bg-fill-2" style={{ aspectRatio: String(one) }} />} /></div></div> : <>
        {(forYou.length > 0 || asking) && <section className="template-section" aria-label="Templates for you" aria-busy={asking && !forYou.length}><Heading>Templates for you</Heading>{forYou.length ? wall(forYou) : <div aria-hidden><Wall items={[1, 0.8]} ratio={one => one} tile={one => <div key={one} className="rounded-[9px] bg-fill-2" style={{ aspectRatio: String(one) }} />} /></div>}</section>}
        {/* The rest waits for what suits the page, so it is never pushed down by a section arriving above it. */}
        {!waitingForMatches && !asking && other.length > 0 && <section className={`template-section${forYou.length ? ' border-t border-separator pt-[14px]' : ''}`} aria-label="Other templates">{forYou.length > 0 && <Heading>Other templates</Heading>}{wall(other)}</section>}
        {!items.length && !failed && <Quiet>{query.trim() ? `No templates found for “${query.trim()}”.` : 'No templates here yet.'}</Quiet>}
        {failed && <div className="catalog-retry"><Quiet tone="error">{failed.problem}</Quiet><button type="button" onClick={reload}>Try again</button></div>}
        {!asking && (hasMore || items.length > 0) && (hasMore || pagination.loading || pagination.problem || Object.values(shelves).every(shelf => shelf.status === 'ready')) && <LibraryPagination label="templates" hasMore={hasMore} loading={pagination.loading || (loading && items.length > 0)} problem={pagination.problem} onMore={() => more(true)} />}
      </>}
    </Body>
  </>;
}

// ── Graphics ────────────────────────────────────────────────────────────────

/** How much of the page's shorter side a drawing lands across. */
const GRAPHIC_SHARE = 0.4;

/** Drawings to decorate a page with: the brand's own first, then what was saved, then Flobi's. */
export function GraphicsPanel({ actions }: { actions: LibraryActions }) {
  const { context, kit } = useWork();
  const project = context?.project ?? null;
  const { query, setQuery, filter, setFilter, source, setSource, categories, shelves, more, reload, hasMore, pagination } = useShelves('graphic', project?.id ?? null, project ? ['project', 'mine', 'workspace', 'flobi'] : ['mine', 'workspace', 'flobi']);
  const [opening, setOpening] = useState<string | null>(null);
  // The brand's graphics are not searched by name — they have none worth searching — so a search shows the catalogue alone.
  const own = kit && !query.trim() ? [...kit.graphics.supergraphics, ...kit.graphics.patterns, ...kit.graphics.characters] : [];
  const showBrand = source === 'all' && !filter && !!kit && own.length > 0;
  const items = Object.values(shelves).flatMap(shelf => shelf.items);
  const loading = Object.values(shelves).some(shelf => shelf.status === 'loading');
  const failed = Object.values(shelves).find(shelf => shelf.status === 'failed');

  const pick = async (item: CatalogItem, acts = actions) => {
    if (item.format === 'svg' && item.svg) {
      acts.placeDrawing(item.svg, { name: item.name, share: GRAPHIC_SHARE });
      return;
    }
    if (opening) return;
    setOpening(item.id);
    try {
      const layers = await catalogLayers(item.id);
      if (layers) acts.placeLayers(layers, { width: item.width, height: item.height });
      else acts.notice('That could not be placed.');
    } catch (error) {
      acts.notice(error instanceof Error ? error.message : 'That could not be placed.');
    } finally {
      setOpening(null);
    }
  };

  return <>
    <CatalogFilters kind="graphic" query={query} onQuery={setQuery} source={source} onSource={setSource} filter={filter} onFilter={setFilter} categories={categories} project={!!project} />
    <Body onEnd={more}>
      {loading && !items.length && !showBrand ? <div role="status" aria-label="Loading graphics"><div aria-hidden><Wall items={[{ id: 0, ratio: 1 }, { id: 1, ratio: 1 }, { id: 2, ratio: 0.8 }, { id: 3, ratio: 1 }]} ratio={one => one.ratio} tile={one => <div key={one.id} className="rounded-[9px] bg-fill-2" style={{ aspectRatio: String(one.ratio) }} />} /></div></div> : <>
        {showBrand && kit && <section className="catalog-section" aria-label="Brand graphics"><Heading>{kit.name}</Heading><KitWall pictures={own} actions={actions} /></section>}
        {items.length > 0 && <section className="catalog-section" aria-label="Graphics">{showBrand && <Heading>More graphics</Heading>}<Wall items={items} ratio={item => item.width / item.height} tile={item => (
          <Removable key={item.id} item={item} actions={actions} onRemoved={reload}>
            <button type="button" aria-label={item.name} aria-busy={opening === item.id} {...tip(item.name)} onClick={() => void pick(item)} {...carry(at => void pick(item, aimed(actions, at)))} className="library-card catalog-card aria-busy:opacity-60">
              <span className="library-card-preview bg-[#f2eee3]" style={{ aspectRatio: String(tileRatio(item.width / item.height)) }}><CatalogPreview item={item} /></span>
            </button>
          </Removable>
        )} /></section>}
        {!loading && !items.length && !showBrand && !failed && <Quiet>{query.trim() ? `No graphics found for “${query.trim()}”.` : 'No graphics here yet.'}</Quiet>}
        {failed && <div className="catalog-retry"><Quiet tone="error">{failed.problem}</Quiet><button type="button" onClick={reload}>Try again</button></div>}
        {(hasMore || items.length > 0) && (hasMore || pagination.loading || pagination.problem || Object.values(shelves).every(shelf => shelf.status === 'ready')) && <LibraryPagination label="graphics" hasMore={hasMore} loading={pagination.loading || (loading && items.length > 0)} problem={pagination.problem} onMore={() => more(true)} />}
      </>}
    </Body>
    <div className="graphics-save-footer"><Keep actions={actions} onSaved={reload} /></div>
  </>;
}
